// studioCards — the Studio's cards, projected from what the workspace already
// holds (Studio v0). Pure: the Library's search rows give each card its title,
// kind and format tag; the Library's items add a source's read state and a
// material's parts. A container is a material whose content includes other
// resources (its `include` nodes, server-projected as `parts`), with its
// preset kind and conformance (`container`, Resource.Container): a module
// holds leaves, a course holds modules and pages.
//
// What is on the canvas: every live source and material, and any other kind as
// a generic card (a raw file today). Packages are outputs, not working
// material, so they stay in the Library. A resource that is a part of a
// container on the canvas is drawn inside that container's card, not on its
// own; a course's modules show their own parts one level down.

export type {
    AlsoIn,
    ContainerCard,
    ContainerPart,
    GenericCard,
    MaterialCard,
    PartKind,
    SourceCard,
    StudioCard,
};
export { containerSummary, isExportable, projectStudioCards };

import type {
    ContainerConformance,
    ContainerKind,
} from "../../app/containerFacts.js";
import type { DocumentFacts } from "../../app/documentFacts.js";
import type { Library, MaterialItem } from "../../app/ports/workspace.js";
import type { MaterialTemplate } from "../../app/ports.js";
import type { SourceReadStatus } from "../../app/sourceReadStatus.js";
import type { SearchResult } from "../Search/searchModel.js";

type SourceCard = {
    readonly type: "source";
    readonly id: string;
    readonly title: string;
    // The file's format tag ("PDF"), "" when the title names none.
    readonly format: string;
    // Absent: the server sent no status (an older server).
    readonly readStatus?: SourceReadStatus;
};

type MaterialCard = {
    readonly type: "material";
    readonly id: string;
    readonly title: string;
    readonly template?: MaterialTemplate;
    readonly aiAuthored: boolean;
    // Planned but not written yet (queued in a generation job).
    readonly planned: boolean;
    // The assistant's, not kept yet: "Suggested" (absent: not so).
    readonly suggestion?: true;
    // Where it stands as a document, and its brief (absent: an older server).
    readonly document?: DocumentFacts;
    readonly basedOn: readonly string[];
    readonly versions?: number;
};

type PartKind = "source" | "material" | "module" | "other";

type ContainerPart = {
    readonly id: string;
    readonly title: string;
    readonly kind: PartKind;
    readonly aiAuthored: boolean;
    // The assistant planned it and the Author has not kept it yet: the
    // "Suggested" mark (absent: theirs, kept, or an older server).
    readonly suggestion?: true;
    // Planned but not written yet: the part fills in when it is.
    readonly planned: boolean;
    // A material part's template, and where it stands as a document with
    // its brief: what it should contain (absent: an older server).
    readonly template?: MaterialTemplate;
    readonly document?: DocumentFacts;
    // A module part's own parts (a course's module, one level down).
    readonly parts: readonly ContainerPart[];
    // How many OTHER containers also hold it, by kind: the same document in
    // several binders must be visible ("Also in 2 modules").
    readonly alsoIn: AlsoIn;
};

type AlsoIn = { readonly modules: number; readonly courses: number };

// The "Suggested" mark, spread onto a part or card the assistant planned.
const SUGGESTION: { readonly suggestion: true } = { suggestion: true };

type ContainerCard = {
    readonly type: "container";
    readonly id: string;
    readonly kind: ContainerKind;
    readonly title: string;
    readonly aiAuthored: boolean;
    // Reading order. A part no longer in the workspace is left out.
    readonly parts: readonly ContainerPart[];
    readonly conformance: ContainerConformance;
};

// Any other kind (a raw file, or a kind this build does not know yet).
type GenericCard = {
    readonly type: "generic";
    readonly id: string;
    readonly title: string;
    // The kind's own word is the view's; this is the format tag, "" if none.
    readonly format: string;
};

type StudioCard = SourceCard | MaterialCard | ContainerCard | GenericCard;

const CONFORMING: ContainerConformance = { type: "conforming" };

// A material's container kind: the server's, else (an older server) a
// material with parts is a module.
const kindOf = (m: MaterialItem | undefined): ContainerKind | undefined =>
    m?.container?.kind ?? ((m?.parts?.length ?? 0) > 0 ? "module" : undefined);

// The cards, in the Library's order. `results` are the Library's search rows
// (titles already resolved); `library` is null until it has loaded.
// `canvas`: what lies on the project's canvas (absent: everything live), so
// a material is drawn inside its container only when that container is on
// this canvas too.
const projectStudioCards = (
    results: readonly SearchResult[],
    library: Library | null,
    canvas?: ReadonlySet<string>,
): readonly StudioCard[] => {
    if (library === null) return [];
    // Conversations live in the chat, packages in the Library.
    const live = results.filter(
        (r) =>
            r.archived !== true &&
            r.kind !== "package" &&
            r.kind !== "conversation",
    );
    const materials = new Map(library.materials.map((m) => [m.id, m]));
    const sources = new Map(library.sources.map((s) => [s.id, s]));
    const titles = new Map(live.map((r) => [r.id, r]));
    const onCanvas = canvas ?? new Set(live.map((r) => r.id));
    // A material drawn inside its container is not drawn again on its own.
    const insideContainer = (m: MaterialItem | undefined): boolean =>
        m?.partOf !== undefined && onCanvas.has(m.partOf.rootId);
    // Every (part, container, kind) holding.
    const holdings = library.materials.flatMap((m) => {
        const kind = kindOf(m);
        return kind === undefined
            ? []
            : (m.parts ?? []).map((part) => ({ part, container: m.id, kind }));
    });
    const alsoIn = (id: string, container: string): AlsoIn => {
        const others = holdings.filter(
            (h) => h.part === id && h.container !== container,
        );
        return {
            modules: others.filter((h) => h.kind === "module").length,
            courses: others.filter((h) => h.kind === "course").length,
        };
    };
    // `depth` 0: a card's own parts; 1: a module part's parts (no deeper).
    // `holder`: the container the part is listed in.
    const partOf =
        (depth: number, holder: string) =>
        (id: string): readonly ContainerPart[] => {
            const r = titles.get(id);
            if (r === undefined) return [];
            const m = materials.get(id);
            const container = kindOf(m) !== undefined;
            return [
                {
                    id,
                    title: r.title,
                    kind: container
                        ? "module"
                        : r.kind === "source" || r.kind === "material"
                          ? r.kind
                          : "other",
                    aiAuthored: r.aiAuthored === true,
                    planned: m?.planned === true,
                    ...(m?.suggestion === true ? SUGGESTION : {}),
                    template: m?.template,
                    document: m?.document,
                    parts:
                        container && depth === 0
                            ? (m?.parts ?? []).flatMap(partOf(depth + 1, id))
                            : [],
                    alsoIn: alsoIn(id, holder),
                },
            ];
        };
    return live.flatMap((r): readonly StudioCard[] => {
        switch (r.kind) {
            case "source":
                return [
                    {
                        type: "source",
                        id: r.id,
                        title: r.title,
                        format: r.meta,
                        readStatus: sources.get(r.id)?.readStatus,
                    },
                ];
            case "material": {
                const m = materials.get(r.id);
                if (insideContainer(m)) return [];
                const kind = kindOf(m);
                if (kind !== undefined)
                    return [
                        {
                            type: "container",
                            id: r.id,
                            kind,
                            title: r.title,
                            aiAuthored: r.aiAuthored === true,
                            parts: (m?.parts ?? []).flatMap(partOf(0, r.id)),
                            conformance:
                                m?.container?.conformance ?? CONFORMING,
                        },
                    ];
                return [
                    {
                        type: "material",
                        id: r.id,
                        title: r.title,
                        template: m?.template,
                        aiAuthored: r.aiAuthored === true,
                        planned: m?.planned === true,
                        ...(m?.suggestion === true ? SUGGESTION : {}),
                        document: m?.document,
                        basedOn: m?.basedOn ?? [],
                        versions: m?.versionCount,
                    },
                ];
            }
            default:
                return [
                    {
                        type: "generic",
                        id: r.id,
                        title: r.title,
                        format: r.meta,
                    },
                ];
        }
    });
};

// What can leave for a learning environment: a written material, or a
// container. A source is input, a generic card an opaque file, and a planned
// material has nothing in it yet.
const isExportable = (card: StudioCard): boolean =>
    card.type === "container" || (card.type === "material" && !card.planned);

// A folded container's summary: how many parts, and how many are still being
// written (planned, queued): a folded container never hides work in progress.
const containerSummary = (
    card: ContainerCard,
): { readonly parts: number; readonly inProgress: number } => ({
    parts: card.parts.length,
    inProgress: card.parts.filter((p) => p.planned).length,
});
